<template>
  <div id="template-applier-div" v-if="(compatibleTemplates && compatibleTemplates.length) || getTemplateId">
    <div class="vform-editor-label mb-1">{{ $t('vform.templates') }}</div>
    <div class="form-error" v-if="object.templateId && !compatibleTemplates.find(item => {return item.key === object.templateId})">
      <icon type="exclamation-triangle" /> {{ $t('vform.missingTemplate') }}<br />
      <div @click="resetTemplate" class="settings-button d-inline-block">{{ $t('vform.removeTemplate') }}</div>
    </div>
    <!--<div class="mb-3">
        <text-input
            ref="textinput"
            type="text"
            :placeholder="$t('typeToSelect')"
            @typingAlert="setSearchQuery"
        />
    </div>-->
    <div class="d-flex">
      <select
          class="custom-select form-text-dark mb-3"
          v-model="object.templateId"
          @change="(evt) => {applyTemplate(evt.target.value)}">
        <option
            v-for="templ in filteredTemplates"
            :key="templ.key"
            :value="templ.key">
          {{ templ.data.templateName }}
        </option>
      </select>
      <div @click="resetTemplate" class="ml-2 lighter">
        <icon type="xmark" />
      </div>
    </div>
    <div v-if="filteredTemplates.length === 0">
      {{ $t('vform.noTemplatesFound') }}
    </div>
    <div class="w-100" />
    <span @click="templatePreviewing = true" class="settings-button d-inline-block mt-2">
      {{ $t('vform.viewAllTemplates') }}
    </span>
    <portal ref="form-popup" to="form-popup">
      <div id="template-preview-vforms" class="p-4" v-if="templatePreviewing">
        <div @click="templatePreviewing = false" class="settings-button d-inline-block mb-5 float-right switched-colors">{{ $t('vform.close') }}</div>
        <template-previewer
          :compatible-templates="compatibleTemplates"
          :selected-template-id="object.templateId"
          @switchToTemplate="applyTemplate"
          @editTemplate="editTemplate"
          @deleteTemplate="(templateKey) => { templateDeleting = compatibleTemplates.find(item => item.key === templateKey); }"
        />
      </div>
    </portal>
    <portal to="vformEditorPopup">
      <popup
        @close="() => {templateDeleting = null;}"
        v-if="templateDeleting"
      >
        <delete-prompt
          slot="popupMainContent"
          custom-message="vform.deleteTemplateQuestion"
          :custom-message-params="{templatename: templateDeleting.data.templateName}"
          @abort="templateDeleting = null"
          @confirm="deleteTemplate(templateDeleting)"
        />
      </popup>
    </portal>
    <portal to="vformEditorPopup">
      <popup
            @close="() => { templateEditing = null; }"
            v-if="templateEditing"
      >
        <div slot="popupMainContent">
            <template-editor
                  :object="object"
                  :pre-selected-template-key="templateEditing"
                  :template-asset-id="templateAssetId"
                  @templateSaved="templateEditing = null"
                  @templateFileChanged="$emit('templateFileChanged')"
              />
        </div>
      </popup>
    </portal>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import {applyTemplate} from "@/components/vForm/templateFunctions";
import Icon from "@/components/Icon.vue";
import Popup from "@/components/Popup.vue";
import TemplatePreviewer from '@/components/vForm/TemplatePreviewer.vue';
import DeletePrompt from "@/components/forms/DeletePrompt";
export default {
  name: 'TemplateApplier',
  components: {
    Icon,
    Popup,
    TemplatePreviewer,
    DeletePrompt,
    // The component doesn't get rendered as a standard component. It has to be rendered dynamically (It's actually better that way, but it's a bit of a pain to work with)
    // Vue is truly a mystery.
    'template-editor': function() {
      return import('@/components/vForm/TemplateEditor.vue');
    }
  },
  props: {
    object: {
      type: Object,
      required: true
    },
    templateAssetId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      searchQuery: '',
      templatePreviewing: false,
      templateEditing: null,
      templateDeleting: null,
      forceReRenderKey: 0, // used to force re-render of the template item list
    };
  },
  computed: {
    ...mapState({
      templates: state => state.formpage.templates
    }),
    compatibleTemplates() {
      if(this.templates) {
        const entries = Object.values(this.templates);

        entries.sort((a, b) => {
          return a.data.templateName.localeCompare(b.data.templateName);
        });

        return entries.filter(item => {
          const {formElementTypeString} = this.object;
          return item.data && item.data.formElementType === formElementTypeString;
        });
      } else {
        return [];
      }
    },
    filteredTemplates() {
      const compatibleTemplates = this.compatibleTemplates;
      const searchQuery = this.searchQuery;

      if(searchQuery === '') {
        return compatibleTemplates;
      }

      return compatibleTemplates.filter(template => {
        return template.data.templateName.toLowerCase().includes(searchQuery.toLowerCase())
      });
    },
    getTemplateId() {
      return this.object.templateId;
    },
    selectedTemplate() {
      if(this.compatibleTemplates && this.object.templateId) {
        const entries = this.compatibleTemplates;
        // find the template that matches the templateId of the object
        // this is used to display the selected template in the template editor
        // and to apply the template to the object
        return entries.find(item => {
          return item.key === this.object.templateId;
        });
      }
      return null;
    },
    selectedTemplateKey() {
      return this.selectedTemplate ? this.selectedTemplate.key : null;
    },
  },
  methods: {
    setSearchQuery(input) {
      this.searchQuery = input.value;
    },
    editTemplate(templateKey) {
      console.log('editTemplate', templateKey);
      this.templateEditing = templateKey;
    },
    async deleteTemplate(template) {
      this.templateDeleting = null;
      const id = template.key;
      await this.$store.dispatch(`clientDeleteAssetPart`, {id: this.templateAssetId, key: id});
      await this.$store.dispatch('loadBlockTemplates', {id: this.templateAssetId});
    },
    resetTemplate() {
      this.object.templateId = null;
      this.forceReRenderKey++;
    },
    applyTemplate(templateKey) {
      const template = this.compatibleTemplates.find(item => item.key === templateKey);
      applyTemplate(this, this.object, template);
      this.forceReRenderKey++;
    },
  },
};
</script>

<style lang="scss" scoped>
#template-preview-vforms {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%,-50%);
  width: 80%;
  height: auto;
  background-color: var(--vform-editor-ui-secondary-color);
  z-index: 655;
  box-shadow: -10px 5px 10px #1f1f1f;
  color: #fff;
}
.chooser-template-container {
  max-height: 100px;
  overflow-y: scroll;
}
</style>
